"use client";

import React, { useEffect } from 'react';
import { Booking } from '@/domain/entities/booking.entity';
import { BookingFlowConfig } from './booking-flow-config';
import { CheckCircle2, QrCode, Phone, Home, ArrowRight, Calendar, Image as ImageIcon, Search, Printer, User, Mail, StickyNote } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Link from 'next/link';
import { motion } from 'framer-motion';
import { format } from 'date-fns';
import { formatMYR } from '@/shared/utils/currency';
import { useReactToPrint } from 'react-to-print';
import { useRef } from 'react';

interface BookingStatusProps {
    booking: Booking;
    config: BookingFlowConfig;
    onReset: () => void;
}

export const BookingStatus: React.FC<BookingStatusProps> = ({ booking, config, onReset }) => {
    const componentRef = useRef<HTMLDivElement>(null);

    const handlePrint = useReactToPrint({
        contentRef: componentRef,
        documentTitle: `Booking-${booking.id}`,
    });

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, []);

    const selectedPackage = booking.package;
    const themeNames = Array.from(new Set(booking.slots.map(slot => slot.theme?.name || 'Theme')));

    return (
        <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="container mx-auto px-4 py-24 max-w-4xl text-center"
        >
            <div className="mb-12 flex justify-center">
                <motion.div
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ delay: 0.2, duration: 0.5 }}
                    className="w-32 h-32 bg-primary/5 rounded-full flex items-center justify-center text-primary relative"
                >
                    <motion.div
                        initial={{ opacity: 0, scale: 0.5 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.4, type: "spring", stiffness: 200 }}
                        className="absolute inset-0 border-2 border-primary/20 rounded-full animate-ping duration-[3s]"
                    />
                    <CheckCircle2 className="w-16 h-16" strokeWidth={1} />
                </motion.div>
            </div>

            <h1 className="text-5xl lg:text-6xl font-serif text-foreground mb-6 leading-tight">
                {config.texts.successTitle}
            </h1>
            <p className="text-muted-foreground mb-12 text-sm max-w-md mx-auto leading-relaxed font-light">
                {config.texts.successDescription}
                {booking.id && (
                    <>
                        {" "}
                        <span className="font-medium text-foreground">Reference #{booking.id}</span>
                    </>
                )}
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
                {/* Booking Summary */}
                {/* Booking Summary - Printable Area */}
                <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.3 }}
                    className="flex flex-col gap-4"
                >
                    <div
                        ref={componentRef}
                        className="bg-card rounded-[2.5rem] p-8 border border-border/50 shadow-[0_20px_60px_-15px_rgba(0,0,0,0.04)] text-left print:shadow-none print:border-none print:p-0"
                    >
                        <div className="flex justify-between items-center mb-6">
                            <h3 className="text-[10px] font-medium uppercase tracking-[0.2em] text-muted-foreground/60 ml-1">Session Invitation</h3>
                            <div className="text-[10px] text-muted-foreground/40 font-mono hidden print:block">
                                REF: {booking.id}
                            </div>
                        </div>

                        <div className="space-y-8">
                            {/* Customer Info */}
                            <div className="flex items-start gap-4 p-4 bg-muted/30 rounded-2xl border border-border/50 print:border-none print:bg-transparent print:p-0">
                                <div className="w-10 h-10 rounded-xl bg-primary/5 flex items-center justify-center text-primary shrink-0 border border-primary/10 print:hidden">
                                    <User className="w-5 h-5" strokeWidth={1.5} />
                                </div>
                                <div className="space-y-1">
                                    <p className="text-sm font-medium text-foreground">{booking.customer.name}</p>
                                    <div className="flex flex-col gap-0.5 text-xs text-muted-foreground font-light">
                                        <div className="flex items-center gap-2">
                                            <Mail className="w-3 h-3 text-primary/40 inline print:hidden" />
                                            <span>{booking.customer.email}</span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <Phone className="w-3 h-3 text-primary/40 inline print:hidden" />
                                            <span>{booking.customer.phone}</span>
                                        </div>
                                        {booking.customer.notes && (
                                            <div className="flex items-start gap-2 mt-1 pt-1 border-t border-border/30">
                                                <StickyNote className="w-3 h-3 text-primary/40 inline mt-0.5 print:hidden" />
                                                <span className="italic text-muted-foreground/80">&quot;{booking.customer.notes}&quot;</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="space-y-6">
                                <div className="flex items-start gap-4">
                                    <div className="w-10 h-10 rounded-xl bg-primary/5 flex items-center justify-center text-primary shrink-0 border border-primary/10 print:hidden">
                                        <ImageIcon className="w-5 h-5" strokeWidth={1.5} />
                                    </div>
                                    <div className="w-full">
                                        <div className="flex justify-between items-start">
                                            <div>
                                                <p className="text-sm font-medium text-foreground">{selectedPackage?.name || 'Package'}</p>
                                                <p className="text-xs text-muted-foreground font-light">{themeNames.join(' & ')}</p>
                                            </div>
                                            <span className="text-sm font-medium text-foreground">{formatMYR(selectedPackage?.finalPrice || 0)}</span>
                                        </div>
                                    </div>
                                </div>

                                <div className="flex items-start gap-4">
                                    <div className="w-10 h-10 rounded-xl bg-primary/5 flex items-center justify-center text-primary shrink-0 border border-primary/10 print:hidden">
                                        <Calendar className="w-5 h-5" strokeWidth={1.5} />
                                    </div>
                                    <div>
                                        <p className="text-sm font-medium text-foreground">
                                            Scheduled Sessions
                                        </p>
                                        <div className="mt-2 space-y-2">
                                            {booking.slots.map((slot, idx) => (
                                                <div key={idx} className="flex flex-col text-xs text-muted-foreground font-light bg-muted/20 p-2 rounded-lg print:bg-transparent print:p-0">
                                                    <span className="font-medium text-foreground/80">
                                                        {format(new Date(slot.date), 'MMMM do, yyyy')} • {slot.time}
                                                    </span>
                                                    <span className="text-[10px] uppercase tracking-wider opacity-70">
                                                        Theme: {slot.theme?.name || 'Default'}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>

                                {booking.addons.length > 0 && (
                                    <div className="pt-4 border-t border-border/50 space-y-3">
                                        <div className="text-[10px] font-medium uppercase tracking-widest text-muted-foreground/60 mb-2">Add-ons</div>
                                        {booking.addons.map(sa => {
                                            const addon = sa.addon;
                                            return (
                                                <div key={sa.addonId} className="flex justify-between items-center text-xs">
                                                    <span className="text-muted-foreground font-light">
                                                        {addon?.name || 'Add-on'} <span className="text-[10px] tabular-nums ml-1 font-medium bg-muted px-1.5 py-0.5 rounded-md">×{sa.quantity}</span>
                                                    </span>
                                                    <span className="font-medium text-foreground">{formatMYR((addon?.price || 0) * sa.quantity)}</span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}

                                <div className="pt-4 border-t border-border/50 flex justify-between items-center">
                                    <span className="text-[10px] font-medium uppercase tracking-widest text-muted-foreground/60">Total Amount</span>
                                    <span className="text-xl font-serif text-foreground">{formatMYR(booking.totalPrice)}</span>
                                </div>

                                <div className="pt-4 border-t border-border/50 flex justify-between items-end">
                                    <div className="flex flex-col mb-0.5">
                                        <span className="text-[10px] font-medium uppercase tracking-widest text-foreground">Booking Deposit (Pay Now)</span>
                                        <span className="text-[10px] text-muted-foreground font-light">Required to secure your reservation</span>
                                    </div>
                                    <span className="text-xl font-serif text-primary">{formatMYR(50)}</span>
                                </div>
                            </div>

                            <div className="border-t border-border/50 pt-4 hidden print:block">
                                <div className="flex flex-col items-center justify-center text-center space-y-2">
                                    <p className="font-serif text-lg text-foreground">LQ Studio</p>
                                    <p className="text-[10px] text-muted-foreground">
                                        Thank you for choosing LQ Studio. Please retain this receipt for your records.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <Button
                        onClick={() => handlePrint()}
                        variant="secondary"
                        className="w-full rounded-2xl h-12 font-medium text-muted-foreground hover:text-foreground transition-all gap-2"
                    >
                        <Printer className="w-4 h-4" />
                        Download PDF Receipt
                    </Button>
                </motion.div>

                {/* Next Steps */}
                <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.4 }}
                    className="bg-card rounded-[2.5rem] p-8 border border-border/50 shadow-[0_20px_60px_-15px_rgba(0,0,0,0.04)] text-left flex flex-col"
                >
                    <h3 className="text-[10px] font-medium uppercase tracking-[0.2em] text-muted-foreground/60 mb-6 ml-1">Necessary Preparations</h3>

                    <div className="space-y-8 flex-1">
                        <div className="flex gap-4">
                            <div className="w-10 h-10 rounded-xl bg-muted/50 flex items-center justify-center text-primary shrink-0 border border-border/50">
                                <QrCode className="w-5 h-5" strokeWidth={1.5} />
                            </div>
                            <div>
                                <p className="text-sm font-serif text-foreground mb-1">Finalize Settlement</p>
                                <p className="text-[11px] text-muted-foreground leading-relaxed font-light">Please complete your transfer via DuitNow QR if you haven&apos;t yet.</p>
                            </div>
                        </div>

                        <div className="flex gap-4">
                            <div className="w-10 h-10 rounded-xl bg-muted/50 flex items-center justify-center text-primary shrink-0 border border-border/50">
                                <Phone className="w-5 h-5" strokeWidth={1.5} />
                            </div>
                            <div>
                                <p className="text-sm font-serif text-foreground mb-1">WhatsApp Verification</p>
                                <p className="text-[11px] text-muted-foreground leading-relaxed font-light">Forward your transaction proof to our support to accelerate approval.</p>
                                <div className="flex flex-col sm:flex-row gap-3 mt-4">
                                    <a
                                        href="https://wa.me/60132688407"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex-1"
                                    >
                                        <Button className="w-full bg-[#25D366] hover:bg-[#20bd5a] text-white rounded-xl h-10 px-4 shadow-lg shadow-[#25D366]/10 transition-all text-[10px] font-medium uppercase tracking-wider">
                                            <Phone className="w-3 h-3 mr-2" />
                                            Fauzi
                                        </Button>
                                    </a>
                                    <a
                                        href="https://wa.me/60187882436"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex-1"
                                    >
                                        <Button className="w-full bg-[#25D366] hover:bg-[#20bd5a] text-white rounded-xl h-10 px-4 shadow-lg shadow-[#25D366]/10 transition-all text-[10px] font-medium uppercase tracking-wider">
                                            <Phone className="w-3 h-3 mr-2" />
                                            Izat
                                        </Button>
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="mt-8 pt-6 border-t border-border/50">
                        <div className="space-y-3 mb-6">
                            <h4 className="text-[10px] uppercase tracking-[0.2em] font-medium text-foreground">Important Notes</h4>
                            <ul className="space-y-2 text-[11px] text-muted-foreground font-light list-disc pl-4">
                                <li>Minimum RM50 deposit. Full payment can be made at the studio or online.</li>
                                <li>The studio can reject / cancel the application if the payment is not made within one hour.</li>
                            </ul>
                        </div>
                        <div className="flex items-center gap-3 text-primary/80">
                            <Search className="w-4 h-4" />
                            <p className="text-[11px] font-medium">Tracking your booking?</p>
                        </div>
                        <p className="text-[11px] text-muted-foreground mt-1 mb-3">You can check your status at any time on our tracking page.</p>
                        <Link href="/tracking">
                            <Button variant="outline" size="sm" className="w-full rounded-xl text-[10px] uppercase tracking-widest font-bold h-10">
                                Visit Tracking Page
                                <ArrowRight className="w-3 h-3 ml-2" />
                            </Button>
                        </Link>
                    </div>
                </motion.div>
            </div >

            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.6 }}
                className="flex flex-col sm:flex-row gap-4 justify-center"
            >
                <Link href="/raya">
                    <Button variant="ghost" className="rounded-full px-10 h-14 text-muted-foreground hover:text-foreground transition-all">
                        <Home className="w-4 h-4 mr-2" />
                        Return to Gallery
                    </Button>
                </Link>
                <Button
                    onClick={onReset}
                    className="rounded-full px-10 h-14 bg-primary text-primary-foreground hover:bg-primary/90 shadow-xl shadow-primary/10 transition-all font-medium"
                >
                    New Reservation
                    <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
            </motion.div>
        </motion.div >
    );
};
